"use client";

import * as NavigationMenuPrimitive from "@radix-ui/react-navigation-menu";
import type { StaticNavSection } from "@/config/nav-links";
import { navIcons } from "./nav-icons";
import { cn } from "@/lib/utils";
import { useNavMobileThemeStyles, useNavThemeStyles } from "./nav-theme-context";
import TrackedLink from "./TrackedLink";

type NavMegaMenuProps = {
    sections?: StaticNavSection[];
    onNavigate?: () => void;
    linkAsChild?: boolean;
    /** Use mobile nav theme tokens (dark drawer on stocks, etc.). */
    useMobileTheme?: boolean;
};

export const NavMegaMenu = ({
    sections = [],
    onNavigate,
    linkAsChild = true,
    useMobileTheme = false,
}: NavMegaMenuProps) => {
    const desktopTheme = useNavThemeStyles();
    const mobileTheme = useNavMobileThemeStyles();
    const theme = useMobileTheme ? mobileTheme : desktopTheme;

    const itemClassName = cn(
        "flex w-full flex-1 items-center gap-3 rounded-[var(--radius-m)] px-4 py-3 text-base font-regular leading-[22px] no-underline outline-none transition-colors",
        theme.megaItem,
    );

    return (
        <div className={cn("flex w-full flex-col gap-10 p-5", theme.mega)}>
            {sections.map((section, sectionIndex) => (
                <div
                    key={section.title ?? `section-${sectionIndex}`}
                    className="flex w-full flex-col gap-5"
                >
                    {section.title && (
                        <p
                            className={cn(
                                "text-base font-semibold uppercase leading-[22px] tracking-[var(--typography-font-letter-spacing-3xl)]",
                                theme.megaSectionTitle,
                            )}
                        >
                            {section.title}
                        </p>
                    )}
                    <ul className="grid w-full grid-cols-1 gap-1 sm:grid-cols-2 md:grid-cols-3">
                        {section.items.map((item) => {
                            const Icon = item.icon ? navIcons[item.icon] : null;
                            const newTabProps = item.openInNewTab
                                ? { target: "_blank" as const, rel: "noopener noreferrer" }
                                : {};
                            const content = (
                                <>
                                    {Icon && (
                                        <Icon
                                            className="size-6 shrink-0"
                                            strokeWidth={1.5}
                                            aria-hidden
                                        />
                                    )}
                                    <span>{item.label}</span>
                                </>
                            );

                            return (
                                <li key={`${item.label}-${item.href}`} className="flex min-w-0">
                                    {linkAsChild ? (
                                        <NavigationMenuPrimitive.Link asChild>
                                            <TrackedLink
                                                href={item.href || "/"}
                                                event={item.eventName}
                                                cleverTapEvent={item.branchEvent}
                                                className={itemClassName}
                                                onClick={onNavigate}
                                                {...newTabProps}
                                            >
                                                {content}
                                            </TrackedLink>
                                        </NavigationMenuPrimitive.Link>
                                    ) : (
                                        <TrackedLink
                                            href={item.href || "/"}
                                            event={item.eventName}
                                            cleverTapEvent={item.branchEvent}
                                            className={itemClassName}
                                            onClick={onNavigate}
                                            {...newTabProps}
                                        >
                                            {content}
                                        </TrackedLink>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                </div>
            ))}
        </div>
    );
};

export default NavMegaMenu;
